import React, { lazy, Suspense, useCallback, useEffect, useState } from "react";
import { useAction } from "@/components/toolkit/InputProvider";
import { runInAction } from "mobx";
import { container } from "@/utils/mobx";
import { Tabs } from "@/components/toolkit/Tabs";
import { FileBrowser } from "@/components/FileBrowser";
import { ContextMenu } from "@/components/toolkit/ContextMenu";
import { Clickable } from "@/components/toolkit/Clickable";
import { NavigationBar } from "@/components/toolkit/NavigationBar";
import { CodeSectionStore } from "./store";
import type { EditorModifier } from "@/components/toolkit/MonacoEditor";
import { appStore } from "@/app_store";
import { t } from "@/i18n";
import { mdToHtml } from "@/utils/markdown";
import { BranchSelect } from "@/components/BranchSelect";
import { SearchPane } from "./c.SearchPane";
import { FileContextMenuDialogs } from "./c.FileContextMenuDialogs";
import { CodeSectionLayout } from "./c.CodeSectionLayout";
import { PumlPreview } from "@/components/toolkit/PumlPreview";
import { MdPreview } from "@/components/toolkit/MdPreview";
import { ConfigureVaultModal } from "@/components/ConfigureVaultModal";
import * as monaco from "monaco-editor";
import "./s.code_section.css";

const MonacoEditor = lazy(() => import("@/components/toolkit/MonacoEditor").then(m => ({ default: m.MonacoEditor })));

export const CodeSection = container((props: {
    store: CodeSectionStore;
}) => {
    const store = props.store;

    const isDirty = store.isFileDirty(store.activeTabPath);
    const isSaving = store.activeTabPath ? store.savingPaths.has(store.activeTabPath) : false;

    const editorModifiers: EditorModifier[] = [
        {
            icon: '▋',
            title: store.showDecorations ? 'Hide change highlights' : 'Show change highlights',
            active: store.showDecorations,
            onClick: () => store.setShowDecorations(!store.showDecorations),
        },
        {
            icon: '↵',
            title: store.wordWrap ? 'Disable word wrap' : 'Enable word wrap',
            active: store.wordWrap,
            onClick: () => store.setWordWrap(!store.wordWrap),
        },
    ];


    const [showVaultModal, setShowVaultModal] = useState(false);
    const [vaultModalActiveId, setVaultModalActiveId] = useState<string | null>(null);

    const collapseRef = React.useRef<((maxDepth: number) => void) | null>(null);

    useAction('codesection', 'close-tab',        () => { if (store.activeTabPath) store.closeTab(store.activeTabPath); }, [store]);
    useAction('codesection', 'prev-tab',         () => store.navigateTab(-1), [store]);
    useAction('codesection', 'next-tab',         () => store.navigateTab(1), [store]);
    useAction('codesection', 'move-tab-left',    () => store.moveActiveTab(-1), [store]);
    useAction('codesection', 'move-tab-right',   () => store.moveActiveTab(1), [store]);
    useAction('codesection', 'collapse-tree',    () => { const next: 0 | 1 = store.collapseLevel === 0 ? 1 : 0; collapseRef.current?.(next); store.toggleCollapseLevel(); }, [store]);
    useAction('codesection', 'close-other-tabs', () => store.closeAllTabsExceptActive(), [store]);
    useAction('codesection', 'save-file',        () => { if (store.activeTabPath) store.saveFile(store.activeTabPath); }, [store]);

    // On mobile the editor and explorer are in an accordion — only one pane is visible at a time.
    // When the global search overlay opens, force the accordion to the sidebar pane (index 0)
    // so the SearchPane is actually visible. Without this the user could trigger search while
    // the editor pane is open and see nothing happen.
    useEffect(() => {
        if (appStore.state.isSearchOpen && store.isMobile) {
            runInAction(() => { store.mobileAccordionIndex = 0; });
        }
    }, [appStore.state.isSearchOpen, store.isMobile]);

    const handleExplorerCollapse = useCallback(() => {
        const next: 0 | 1 = store.collapseLevel === 0 ? 1 : 0;
        collapseRef.current?.(next);
        store.toggleCollapseLevel();
    }, []);

    const handleFileContextMenu = useCallback((path: string, isDir: boolean, x: number, y: number) => {
        const items = [
            { title: t("file_create_folder_title"), value: "create_folder" },
            { title: t("file_create_file_title"), value: "create_file" },
            { title: t("file_rename_title"), value: "rename" },
            { title: t("file_delete_btn"), value: "delete" },
        ];
        if (!isDir) {
            items.unshift({ title: t("file_add_to_context"), value: "add_to_context" });
        }
        store.contextMenuStore.show(x, y, items, (value: string) => {
            const parentPath = isDir ? path : path.split("/").slice(0, -1).join("/");
            if (value === "rename") store.openRenameDialog(path);
            else if (value === "delete") { store.deleteConfirmPath = path; }
            else if (value === "add_to_context") { appStore.state.addContextFile(path); }
            else if (value === "create_folder") { store.openCreateFolderDialog(parentPath); }
            else if (value === "create_file") { store.openCreateFileDialog(parentPath); }
        });
    }, [store]);

    const handleBackgroundContextMenu = useCallback((x: number, y: number) => {
        const items = [
            { title: t("file_create_folder_title"), value: "create_folder" },
            { title: t("file_create_file_title"), value: "create_file" },
        ];
        store.contextMenuStore.show(x, y, items, (value: string) => {
            const rootPath = store.fileBrowserRootPath;
            if (value === "create_folder") store.openCreateFolderDialog(rootPath);
            else if (value === "create_file") store.openCreateFileDialog(rootPath);
        });
    }, [store]);

    // Pane content shared between Split (desktop) and Accordion (mobile)
    const leftPane = (
        <div className="code_section__pane code_section__pane--sidebar">
            {appStore.state.isSearchOpen ? (
                <SearchPane onClose={() => appStore.state.closeSearch()} />
            ) : (
                <>
                    {!store.isMobile && (
                        <div className="code_section__pane_label">
                            <span className="code_section__pane_label_text">{t("code_pane_explorer")}</span>
                            <BranchSelect />
                            <Clickable onClick={handleExplorerCollapse}>
                                <button
                                    className="code_section__collapse_btn"
                                    title={t("code_collapse_tree")}
                                >⊟</button>
                            </Clickable>
                        </div>
                    )}
                    {store.isMobile && store.isLandscape && (
                        <div className="branch_select_row">
                            <BranchSelect />
                        </div>
                    )}
                    {store.isMerging && (
                        <div className="code_section__merge_banner" role="status" title={t("review_merge_in_progress_banner")}>
                            ⚠ {t("code_merge_in_progress")}
                        </div>
                    )}
                    <div className="code_section__browser_wrap">
                        {appStore.agent.isConnected && (
                            <FileBrowser
                                mode="tree"
                                clickMode="single"
                                rootPath={store.fileBrowserRootPath}
                                sandbox={appStore.state.vaultMode
                                    ? (appStore.agent.session.project?.vault_sandbox ?? null)
                                    : appStore.agent.session.container?.sandbox ?? null}
                                activePath={store.activeTabPath}
                                onOpen={(path: string, isDir: boolean) => { if (!isDir) store.openFile(path); }}
                                onContextMenu={handleFileContextMenu}
                                onBackgroundContextMenu={handleBackgroundContextMenu}
                                collapseRef={collapseRef}
                            />
                        )}
                    </div>
                    {appStore.state.vaultMode && (
                        <button className="configure_vault_banner" onClick={() => setShowVaultModal(true)}>
                            {t("vault_configure_banner")}
                        </button>
                    )}
                </>
            )}
        </div>
    );

    const rightPane = (
        <div className="code_section__pane">
            <Tabs
                leftSlot={!store.isMobile ? <NavigationBar /> : undefined}
                tabs={store.openTabs}
                activeTabPath={store.activeTabPath}
                onTabChange={(path: string) => store.setActiveTab(path)}
                onTabClose={(path: string) => store.closeTab(path)}
                onTabCloseAll={() => store.closeAllTabs()}
                onTabCloseOthers={(path) => { store.setActiveTab(path); store.closeAllTabsExceptActive(); }}
                onTabReorder={(from, to) => store.reorderTabs(from, to)}
            >
                {!store.activeTabPath && (
                    <div className="code_section__no_tabs">{t("code_no_files")}</div>
                )}
                {store.activeTabPath && store.isActiveFileDeleted && (
                    <div className="code_section__no_tabs">{t("code_file_deleted")}</div>
                )}
                {store.activeTabPath && !store.isActiveFileDeleted && !store.fileContents.has(store.activeTabPath) && (
                    <div className="code_section__loading">
                        <div className="code_section__loading_spinner" />
                    </div>
                )}
                <div style={store.previewVisible || store.pumlPreviewVisible ? { display: "none" } : { display: "contents" }}>
                    <Suspense fallback={null}>
                        <MonacoEditor
                            store={store.monacoEditorStore}
                            value={store.activeTabPath ? (store.fileContents.get(store.activeTabPath) ?? "") : ""}
                            language={store.activeTabLanguage}
                            readOnly={!store.isInEditMode(store.activeTabPath ?? "")}
                            onChange={(v: string) => { if (store.activeTabPath) store.setFileContent(store.activeTabPath, v); }}
                            enableConflictLens
                            getConflicts={() => store.getActiveConflicts()}
                            stashConflict={store.stashConflict}
                            saveNow={() => store.saveActive()}
                            viewStateKey={store.activeTabPath ?? undefined}
                            scrollSyncKey={store.activeRelativePath ?? undefined}
                            scrollSyncSection="code"
                            disableContextMenu={!store.isInEditMode(store.activeTabPath ?? "")}
                            disableSelection={!store.isInEditMode(store.activeTabPath ?? "")}
                            fontSize={store.editorFontSize}
                            onFontSizeChange={(size: number) => store.setEditorFontSize(size)}
                            hidden={!store.activeTabPath || store.isActiveFileDeleted || !store.fileContents.has(store.activeTabPath)}
                            decorationRanges={store.showDecorations ? store.activeTabDecorations : []}
                            wordWrap={store.wordWrap}
                            modifiers={editorModifiers}
                            onCtrlClickSymbol={(symbol, line, col) => {
                                if (store.activeTabPath) store.goToDefinition(symbol, line, col, store.activeTabPath);
                            }}
                            scrollToLine={store.pendingScrollToLine}
                            onScrollToLineDone={() => store.clearScrollToLine()}
                        />
                    </Suspense>
                </div>
                {store.previewVisible && store.activeTabPath && store.fileContents.has(store.activeTabPath) && (
                    <MdPreview
                        html={mdToHtml(store.fileContents.get(store.activeTabPath) ?? "")}
                        fontSize={store.editorFontSize}
                        onFontSizeChange={store.setEditorFontSize}
                    />
                )}
                {store.pumlPreviewVisible && store.activeTabPath && store.fileContents.has(store.activeTabPath) && (
                    <PumlPreview isPuml={store.isPuml} content={store.pumlContent} active={store.pumlPreviewActive} />
                )}
            </Tabs>
            {store.activeTabPath && (store.isMarkdown || store.isPuml || isDirty) && (
                <div className="code_section__md_overlay">
                    {store.isMarkdown && (
                        <>
                            <Clickable onClick={() => store.setMdPreviewActive(false)}>
                                <button className={"code_section__md_btn" + (!store.mdPreviewActive ? " code_section__md_btn--active" : "")}>
                                    Raw
                                </button>
                            </Clickable>
                            <Clickable onClick={() => store.setMdPreviewActive(true)}>
                                <button className={"code_section__md_btn" + (store.mdPreviewActive ? " code_section__md_btn--active" : "")}>
                                    Preview
                                </button>
                            </Clickable>
                        </>
                    )}
                    {store.isPuml && (
                        <>
                            <Clickable onClick={() => store.setPumlPreviewActive(false)}>
                                <button className={"code_section__md_btn" + (!store.pumlPreviewActive ? " code_section__md_btn--active" : "")}>
                                    Raw
                                </button>
                            </Clickable>
                            <Clickable onClick={() => store.setPumlPreviewActive(true)}>
                                <button className={"code_section__md_btn" + (store.pumlPreviewActive ? " code_section__md_btn--active" : "")}>
                                    Preview
                                </button>
                            </Clickable>
                        </>
                    )}
                    {isDirty && (
                        <Clickable onClick={() => !isSaving && store.saveFile(store.activeTabPath!)}>
                            <button
                                className={"code_section__md_btn code_section__save_btn" + ((store.isMarkdown || store.isPuml) ? " code_section__save_btn--sep" : "")}
                                disabled={isSaving}
                            >
                                {isSaving ? <span className="code_section__save_spinner" /> : "Save"}
                            </button>
                        </Clickable>
                    )}
                </div>
            )}
            {store.activeRelativePath && (
                <button
                    className={"code_section__context_btn" + (appStore.state.isContextFile(store.activeRelativePath) ? " code_section__context_btn--active" : "")}
                    onClick={() => appStore.state.toggleContextFile(store.activeRelativePath!)}
                    title={appStore.state.isContextFile(store.activeRelativePath) ? "Remove from agent context" : "Add to agent context"}
                >@</button>
            )}
        </div>
    );

    const overlays = (
        <>
            <ContextMenu store={store.contextMenuStore} options={store.contextMenuStore.options ?? []} />
            <FileContextMenuDialogs store={store} />
            {showVaultModal && appStore.agent.session.project && (
                <ConfigureVaultModal
                    project={appStore.agent.session.project}
                    activeId={vaultModalActiveId}
                    onActiveIdChange={setVaultModalActiveId}
                    onClose={() => setShowVaultModal(false)}
                />
            )}
        </>
    );

    return (
        <CodeSectionLayout
            store={store}
            leftPane={leftPane}
            rightPane={rightPane}
            overlays={overlays}
            onExplorerCollapse={handleExplorerCollapse}
        />
    );
}, CodeSectionStore);
